
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useBulkCreateDistributionCenterPrices } from '@/hooks/use-distribution-center-prices';
import { useDistributionCenterExcelParser } from './useDistributionCenterExcelParser';
import { useBulkUploadProgress } from '@/components/farm-prices/upload/useBulkUploadProgress';
import DistributionCenterUploadTemplate from './DistributionCenterUploadTemplate';
import DistributionCenterPreviewTable from './DistributionCenterPreviewTable';

interface DistributionCenterUploadProps {
  open: boolean;
  onClose: () => void;
}

const DistributionCenterUpload: React.FC<DistributionCenterUploadProps> = ({ 
  open, 
  onClose 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  
  const { 
    processFile, 
    clearData, 
    isProcessing, 
    previewData, 
    errors, 
    hasData, 
    hasErrors 
  } = useDistributionCenterExcelParser();
  
  const bulkCreateMutation = useBulkCreateDistributionCenterPrices();
  const { uploadProgress, resetUpload } = useBulkUploadProgress();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setUploadStatus('idle');
    await processFile(file);
  };

  const handleUpload = async () => {
    if (!hasData || previewData.length === 0) return;

    setUploadStatus('uploading');
    resetUpload();

    try {
      const batchSize = 50;
      const totalBatches = Math.ceil(previewData.length / batchSize);
      
      for (let i = 0; i < totalBatches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, previewData.length);
        const batch = previewData.slice(start, end);
        
        await bulkCreateMutation.mutateAsync(batch);
      }

      setUploadStatus('success');
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    clearData();
    resetUpload();
    onClose();
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'uploading':
        return <Upload className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <FileSpreadsheet className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusMessage = () => {
    switch (uploadStatus) {
      case 'success':
        return `Successfully uploaded ${previewData.length} distribution center prices!`;
      case 'error':
        return 'Upload failed. Please try again.';
      case 'uploading':
        return `Uploading ${previewData.length} prices...`;
      default:
        return '';
    }
  };

  const currentProgress = uploadStatus === 'uploading' ? 
    Math.round((uploadProgress.current / uploadProgress.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Distribution Center Prices</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Download */}
          <DistributionCenterUploadTemplate />

          {/* File Upload */}
          <div>
            <Label htmlFor="file-upload">Select Excel File</Label>
            <Input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              disabled={isProcessing || uploadStatus === 'uploading'}
            />
          </div>

          {/* Processing Status */}
          {isProcessing && (
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4 animate-spin" />
              <span>Processing file...</span>
            </div>
          )}

          {/* Upload Status */}
          {uploadStatus !== 'idle' && (
            <Alert>
              <div className="flex items-center gap-2">
                {getStatusIcon()}
                <AlertDescription>{getStatusMessage()}</AlertDescription>
              </div>
            </Alert>
          )}

          {/* Upload Progress */}
          {uploadStatus === 'uploading' && (
            <div className="space-y-2">
              <Progress value={currentProgress} />
              <p className="text-sm text-gray-600">
                {uploadProgress.current} of {uploadProgress.total} prices uploaded
              </p>
            </div>
          )}

          {/* Errors */}
          {hasErrors && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Found {errors.length} error(s):</p>
                  <ul className="list-disc list-inside space-y-1">
                    {errors.slice(0, 5).map((error, index) => (
                      <li key={index} className="text-sm">{error}</li>
                    ))}
                    {errors.length > 5 && (
                      <li className="text-sm font-medium">...and {errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Table */}
          {hasData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Preview ({previewData.length} prices)
                </h3>
                <Button
                  onClick={handleUpload}
                  disabled={uploadStatus === 'uploading' || hasErrors}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Upload Prices
                </Button>
              </div>
              <DistributionCenterPreviewTable data={previewData.slice(0, 10)} />
              {previewData.length > 10 && (
                <p className="text-sm text-gray-600">
                  Showing first 10 of {previewData.length} prices
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DistributionCenterUpload;
